import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, Subject} from 'rxjs';

/**
 * Response from POST /api/uploads/init or POST /api/uploads/intake/init.
 */
export interface UploadInitResponse {
  upload_id: string;
  presigned_url: string;
  staging_key: string;
  expires_at: string;
}

/**
 * Response from POST /api/uploads/finalize or POST /api/uploads/intake/finalize.
 */
export interface UploadFinalizeResponse {
  status: string;
  upload_id: string;
  original_format?: string;
  content_length?: number;
  reason?: string;
}

/**
 * UploadClient encapsulates the upload-on-select HTTP calls and the direct PUT
 * to the presigned S3 URL.
 *
 * - Authenticated uploads hit /api/uploads/*
 * - Anonymous intake uploads hit /api/uploads/intake/*
 *
 * The distinction is made by the presence of `attorney_org_id` in the opts parameter.
 */
@Injectable({providedIn: 'root'})
export class UploadClient {

  constructor(
    private http: HttpClient,
    @Inject('environment') private environment
  ) {}

  /**
   * Initialize an upload session.
   *
   * If `opts.attorney_org_id` is provided, uses the anonymous intake endpoint
   * (POST /api/uploads/intake/init), gated by AWS WAF at the edge (the `x-aws-waf-token`
   * header is added centrally by WafTokenInterceptor). Otherwise uses the authenticated
   * endpoint (POST /api/uploads/init).
   */
  init(
    file: File,
    opts: { attorney_org_id?: string }
  ): Observable<UploadInitResponse> {
    if (opts.attorney_org_id) {
      // Anonymous intake path
      return this.http.post<UploadInitResponse>(
        `${this.environment.api_prefix}api/uploads/intake/init`,
        {
          attorney_org_id: opts.attorney_org_id,
          filename: file.name,
          content_length: file.size,
          content_type: file.type || 'application/octet-stream'
        }
      );
    }

    // Authenticated path
    return this.http.post<UploadInitResponse>(
      `${this.environment.api_prefix}api/uploads/init`,
      {
        filename: file.name,
        content_length: file.size,
        content_type: file.type || 'application/octet-stream'
      }
    );
  }

  /**
   * PUT file bytes directly to the presigned S3 URL.
   *
   * Uses XMLHttpRequest (not Angular HttpClient) because the presigned URL is a
   * cross-origin request to S3, not the app server. Tracks upload progress via
   * the `progress` Subject (0–100%). The `abort` AbortController allows
   * cancellation of the in-flight PUT.
   */
  put(
    url: string,
    file: File,
    abort: AbortController,
    progress: Subject<number>
  ): Observable<void> {
    return new Observable<void>(subscriber => {
      const xhr = new XMLHttpRequest();

      // Listen for abort signal
      const onAbort = () => {
        xhr.abort();
      };
      abort.signal.addEventListener('abort', onAbort);

      xhr.upload.addEventListener('progress', (event: ProgressEvent) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          progress.next(percent);
        }
      });

      xhr.addEventListener('load', () => {
        abort.signal.removeEventListener('abort', onAbort);
        if (xhr.status >= 200 && xhr.status < 300) {
          progress.next(100);
          subscriber.next();
          subscriber.complete();
        } else {
          subscriber.error(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        abort.signal.removeEventListener('abort', onAbort);
        subscriber.error(new Error('Upload failed due to a network error'));
      });

      xhr.addEventListener('abort', () => {
        abort.signal.removeEventListener('abort', onAbort);
        subscriber.error(new Error('Upload was aborted'));
      });

      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.send(file);

      // Teardown: abort the XHR if the Observable is unsubscribed
      return () => {
        abort.signal.removeEventListener('abort', onAbort);
        if (xhr.readyState !== XMLHttpRequest.DONE) {
          xhr.abort();
        }
      };
    });
  }

  /**
   * Finalize an upload session after the PUT completes.
   *
   * If `opts.attorney_org_id` is provided, uses the anonymous intake endpoint
   * (POST /api/uploads/intake/finalize).
   * Otherwise, uses the authenticated endpoint (POST /api/uploads/finalize).
   */
  finalize(
    upload_id: string,
    opts: { attorney_org_id?: string }
  ): Observable<UploadFinalizeResponse> {
    if (opts.attorney_org_id) {
      // Anonymous intake path
      return this.http.post<UploadFinalizeResponse>(
        `${this.environment.api_prefix}api/uploads/intake/finalize`,
        {
          upload_id,
          attorney_org_id: opts.attorney_org_id
        }
      );
    }

    // Authenticated path
    return this.http.post<UploadFinalizeResponse>(
      `${this.environment.api_prefix}api/uploads/finalize`,
      {upload_id}
    );
  }

  /**
   * Delete a staged upload. Best-effort; used when a user removes a file row.
   */
  delete(upload_id: string): Observable<void> {
    return this.http.delete<void>(
      `${this.environment.api_prefix}api/uploads/${upload_id}`
    );
  }

  /**
   * Orchestrates the full upload-on-select cycle for a single file and resolves with the
   * `upload_id` once the staged object is scanned clean ("ready").
   *
   * init → PUT to presigned URL → poll finalize (exponential backoff) until a terminal
   * scan state. Resolves with `{ upload_id, original_format }` on "ready"; errors on
   * infected/rejected/failed/timeout. Intended for callers (parse wizards, bulk import)
   * that previously POSTed a FormData and now need a pre-staged, pre-scanned object.
   *
   * No file bytes pass through the app server — the PUT goes directly to S3.
   */
  uploadAndFinalize(
    file: File,
    opts: { attorney_org_id?: string } = {}
  ): Observable<{ upload_id: string; original_format: string }> {
    const POLL_INITIAL_DELAY = 2000;
    const POLL_MULTIPLIER = 1.5;
    const POLL_MAX_DELAY = 10000;
    const POLL_WALL_CLOCK_LIMIT = 90000;

    return new Observable<{ upload_id: string; original_format: string }>(subscriber => {
      let uploadId: string | null = null;
      let cancelled = false;
      // Sole gate on the teardown's cleanup delete below: every OTHER exit (ready, infected, rejected,
      // failed, the wall-clock timeout) sets this before its own subscriber.error/complete, specifically so
      // that call doesn't happen twice (the timeout branch already deletes for its own reason) and so a
      // "ready" upload the caller is about to attach doesn't get deleted out from under it the instant this
      // subscription is torn down after completing normally.
      let settled = false;
      let timer: ReturnType<typeof setTimeout> | null = null;
      const abort = new AbortController();
      const progress = new Subject<number>();
      const startTime = Date.now();

      const finalizeOpts = opts.attorney_org_id ? { attorney_org_id: opts.attorney_org_id } : {};

      const poll = (delay: number) => {
        timer = setTimeout(() => {
          if (cancelled || !uploadId) {
            return;
          }
          if (Date.now() - startTime >= POLL_WALL_CLOCK_LIMIT) {
            settled = true;
            this.delete(uploadId).subscribe({ error: () => { /* best-effort */ } });
            subscriber.error(new Error('Scan timed out, please try again'));
            return;
          }
          this.finalize(uploadId, finalizeOpts).subscribe({
            next: (res: UploadFinalizeResponse) => {
              if (cancelled) {
                return;
              }
              switch (res.status) {
                case 'ready':
                  settled = true;
                  subscriber.next({ upload_id: res.upload_id, original_format: res.original_format ?? '' });
                  subscriber.complete();
                  break;
                case 'scanning':
                  poll(Math.min(delay * POLL_MULTIPLIER, POLL_MAX_DELAY));
                  break;
                case 'infected':
                  settled = true;
                  subscriber.error(new Error('The file appears to be infected and was rejected.'));
                  break;
                case 'rejected':
                  settled = true;
                  subscriber.error(new Error(res.reason ?? 'The file was rejected.'));
                  break;
                default:
                  settled = true;
                  subscriber.error(new Error('Upload failed during scanning.'));
                  break;
              }
            },
            error: (err) => {
              if (!cancelled) {
                settled = true;
                subscriber.error(err);
              }
            }
          });
        }, delay);
      };

      this.init(file, opts).subscribe({
        next: (initRes: UploadInitResponse) => {
          if (cancelled) {
            return;
          }
          // Guard against a WAF silent-challenge / empty init body (2xx, no upload_id): surface a
          // clean error instead of throwing "Cannot read properties of null (reading 'upload_id')".
          if (!initRes?.upload_id) {
            settled = true;
            subscriber.error(new Error('Upload could not be started, please try again.'));
            return;
          }
          uploadId = initRes.upload_id;
          this.put(initRes.presigned_url, file, abort, progress).subscribe({
            next: () => {
              if (!cancelled) {
                poll(POLL_INITIAL_DELAY);
              }
            },
            error: (err) => {
              if (!cancelled) {
                settled = true;
                subscriber.error(err);
              }
            }
          });
        },
        error: (err) => {
          if (!cancelled) {
            settled = true;
            subscriber.error(err);
          }
        }
      });

      // Teardown on unsubscribe: stop polling, abort in-flight PUT, best-effort delete of the staged
      // object — but only when torn down early (a caller unsubscribing, e.g. a user removing the row while
      // it's still uploading/scanning), not on every teardown: RxJS runs this same function after a normal
      // complete/error too, and `settled` is what tells those two situations apart. Without it, the staged
      // upload from a cancelled-mid-flight row would sit around unreferenced indefinitely.
      return () => {
        cancelled = true;
        if (timer) {
          clearTimeout(timer);
        }
        abort.abort();
        if (!settled && uploadId) {
          this.delete(uploadId).subscribe({ error: () => { /* best-effort */ } });
        }
      };
    });
  }
}
